/**
 * Clipboard and shortcut decisions for the Worker terminal (#515), made away from the terminal.
 *
 * Pure: key events and text in, decisions and words out. Nothing here touches the clipboard,
 * the terminal or the network. The terminal component does exactly what these say, and every
 * byte it pastes still goes through the one guarded owner-input path (xterm `paste()` ->
 * `onData` -> `writeTerminalInput`), never around it.
 */

/** A paste this long is shown before it is sent, whatever it contains. */
export const PASTE_LARGE_CHARS = 2000;
/** How many lines of a paste the preview shows. */
export const PASTE_PREVIEW_LINES = 12;

export type TerminalPlatform = 'mac' | 'other';

/** Which platform's shortcut conventions apply, from what the browser says it is. */
export function terminalPlatform(nav: { readonly platform?: string; readonly userAgent?: string } | undefined): TerminalPlatform {
  const text = `${nav?.platform ?? ''} ${nav?.userAgent ?? ''}`;
  return /Mac|iPhone|iPad|iPod/i.test(text) ? 'mac' : 'other';
}

export interface TerminalKeyLike {
  readonly type: string;
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

/**
 * What a key press means for copy and paste.
 *
 * - `copy`: copy the terminal selection; the key must not reach the program.
 * - `native_paste`: let the browser raise its paste event (which the terminal routes through
 *   its confirmation and the guarded input path); the key itself must not reach the program.
 * - `swallow`: a copy chord with nothing selected; it copies nothing and sends nothing.
 * - `pass`: an ordinary terminal key - Ctrl+C interrupt, Tab, arrows - untouched.
 */
export type TerminalShortcut = 'copy' | 'native_paste' | 'swallow' | 'pass';

export function terminalShortcut(
  event: TerminalKeyLike,
  platform: TerminalPlatform,
  hasSelection: boolean,
): TerminalShortcut {
  if (event.type !== 'keydown') return 'pass';
  const key = event.key.toLowerCase();
  if (key !== 'c' && key !== 'v') return 'pass';

  // Cmd+C / Cmd+V on a Mac: never a terminal byte.
  if (platform === 'mac' && event.metaKey && !event.ctrlKey && !event.altKey) {
    if (key === 'c') return hasSelection ? 'copy' : 'swallow';
    return 'native_paste';
  }
  // Ctrl+Shift+C / Ctrl+Shift+V: the terminal convention on every platform.
  if (event.ctrlKey && event.shiftKey && !event.metaKey && !event.altKey) {
    if (key === 'c') return hasSelection ? 'copy' : 'swallow';
    return 'native_paste';
  }
  // Plain Ctrl+C: interrupt, always, on a Mac. Elsewhere it copies only when something is
  // actually selected (as Windows Terminal does); with no selection it is still Ctrl+C.
  if (key === 'c' && event.ctrlKey && !event.shiftKey && !event.metaKey && !event.altKey) {
    return platform === 'other' && hasSelection ? 'copy' : 'pass';
  }
  return 'pass';
}

/** Why a paste is shown before it is sent. */
export type PasteRisk = 'multiline' | 'trailing_newline' | 'control_characters' | 'large';

export interface PasteAssessment {
  readonly text: string;
  readonly length: number;
  readonly lines: number;
  readonly trailingNewline: boolean;
  /** Printable names of the control characters found, e.g. ["ESC", "Ctrl+D"]. Tab is not one. */
  readonly controlCharacters: readonly string[];
  readonly risks: readonly PasteRisk[];
  /** True when the paste must be confirmed before a single byte is sent. */
  readonly needsConfirmation: boolean;
}

const LINE_BREAK = /\r\n|\r|\n/;

function controlName(code: number): string {
  if (code === 0x1b) return 'ESC';
  if (code === 0x7f) return 'DEL';
  if (code === 0x00) return 'NUL';
  if (code >= 0x80 && code <= 0x9f) return `C1 0x${code.toString(16).toUpperCase()}`;
  return `Ctrl+${String.fromCharCode(code + 64)}`;
}

/** What a clipboard payload would do if it were typed into a terminal. The text is not changed. */
export function assessPaste(text: string): PasteAssessment {
  const lines = text === '' ? 0 : text.split(LINE_BREAK).length - (/(\r\n|\r|\n)$/.test(text) ? 1 : 0);
  const trailingNewline = /(\r\n|\r|\n)$/.test(text);
  const found = new Set<string>();
  for (const char of text) {
    const code = char.codePointAt(0)!;
    if (code === 0x09 || code === 0x0a || code === 0x0d) continue;
    if (code < 0x20 || code === 0x7f || (code >= 0x80 && code <= 0x9f)) found.add(controlName(code));
  }
  const risks: PasteRisk[] = [];
  if (lines > 1) risks.push('multiline');
  if (trailingNewline) risks.push('trailing_newline');
  if (found.size > 0) risks.push('control_characters');
  if (text.length > PASTE_LARGE_CHARS) risks.push('large');
  return {
    text,
    length: text.length,
    lines,
    trailingNewline,
    controlCharacters: [...found].sort(),
    risks,
    needsConfirmation: risks.length > 0,
  };
}

/** One sentence per reason, in the owner's words. */
export function pasteRiskLines(assessment: PasteAssessment, bracketedPaste: boolean): readonly string[] {
  const out: string[] = [];
  if (assessment.risks.includes('multiline')) {
    out.push(bracketedPaste
      ? `${assessment.lines} lines. The program has bracketed paste on, so they should arrive as one block rather than run one by one.`
      : `${assessment.lines} lines. Each line break is sent as Enter and may run a command.`);
  }
  if (assessment.risks.includes('trailing_newline')) {
    out.push('Ends with a line break: the last line will be submitted as soon as it is pasted.');
  }
  if (assessment.risks.includes('control_characters')) {
    out.push(`Contains control characters (${assessment.controlCharacters.join(', ')}). They are sent exactly as copied.`);
  }
  if (assessment.risks.includes('large')) {
    out.push(`${assessment.length.toLocaleString('en-US')} characters.`);
  }
  out.push('Line breaks are sent as Enter, as any terminal paste does. Nothing else is changed.');
  return out;
}

/**
 * The paste made visible for review: control characters drawn as symbols, lines clipped to
 * the preview length. This is only what is shown - the payload sent is the original text.
 */
export function pastePreview(text: string, maxLines = PASTE_PREVIEW_LINES): { readonly shown: string; readonly hiddenLines: number } {
  const visible = [...text].map((char) => {
    const code = char.codePointAt(0)!;
    if (code === 0x0a || code === 0x0d || code === 0x09) return char;
    if (code < 0x20) return String.fromCodePoint(0x2400 + code);
    if (code === 0x7f) return '␡';
    return char;
  }).join('');
  const lines = visible.split(/\r\n|\r|\n/);
  if (lines.at(-1) === '' && lines.length > 1) {
    lines.pop();
    lines[lines.length - 1] = `${lines.at(-1)}⏎`;
  }
  const shown = lines.slice(0, maxLines).join('\n');
  return { shown, hiddenLines: Math.max(0, lines.length - maxLines) };
}

/** The text without its final line break, for the owner's explicit "paste without it" choice. */
export function withoutTrailingNewline(text: string): string {
  return text.replace(/(\r\n|\r|\n)$/, '');
}

/** Why Cut is not offered: a terminal's output cannot be removed by selecting it. */
export const TERMINAL_CUT_REASON =
  'Cut is not available: a terminal selection is output the program already printed, so it can be copied but not removed.';

/** A clipboard failure, said plainly, with the route that still works. */
export function clipboardFailureText(action: 'copy' | 'paste', error: unknown): string {
  const name = error instanceof Error || (typeof error === 'object' && error !== null && 'name' in error)
    ? String((error as { name?: unknown }).name ?? '')
    : '';
  const fallback = action === 'copy'
    ? 'Select the text and press Cmd+C (Mac) or Ctrl+Shift+C instead.'
    : 'Click the terminal and press Cmd+V (Mac) or Ctrl+Shift+V instead; multi-line text is still shown before it is sent.';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return `The browser did not allow Brunel to ${action === 'copy' ? 'write to' : 'read'} the clipboard. ${fallback}`;
  }
  if (name === 'unavailable') {
    return `This browser does not let pages ${action === 'copy' ? 'write to' : 'read'} the clipboard here. ${fallback}`;
  }
  return `${action === 'copy' ? 'Copy' : 'Paste'} failed: the clipboard could not be ${action === 'copy' ? 'written' : 'read'}. ${fallback}`;
}
